import FeatherIcon from "feather-icons-react";
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { APPLICATION_STATUS, RoutesFullPath } from "../../utils/constants";
import AdminRejectAanvraagModal from "../../components/reason-modal";
import { getApplicationById, adminUpdateApplicationStatus, fetchUserCompanyDetails, adminSetApplicationPartner, adminFetchBtwStatuses, adminUpdateApplicationWorkflowStatus, adminFetchAdmins, adminUpdateApplicationAssignee, adminAddApplicationComment, adminEditApplicationComment, adminDeleteApplicationComment, adminApplicationMailContext, adminSendApplicationCustomerEmail, adminFetchBtwEmailTemplates, adminCreateApplicationInvoice } from "../../core/axios/endpoints";
import BtwStatusMenu from "../../components/btw-status-menu";
import BtwAssigneeMenu from "../../components/btw-assignee-menu";
import BtwActivityFeed from "../../components/btw-activity-feed";
import SendCustomerEmailModal from "../../components/send-customer-email-modal";
import { useUser } from "../../core/context/user";
import PageLoader from "../../components/page-loader";
import { toastrError, toastrSuccess } from "../../utils/alertify";
import KvkDetailsModal from "../../components/kvk-details-model";
import { PARTNER_INVOICE_LABELS, partnerNameOf } from "../../core/partner-subsidie";
import ApplicationPartnerModal from "../../components/application-partner-modal";
import ConfirmModal from "../../components/confirm-modal";
import EmailLogList from "../../components/email-log-list";
import EmailPreviewModal from "../../components/email-preview-modal";

// Lozenge-kleuren per subsidie-status (alleen presentatie).
const STATUS_LOZENGE = {
    [APPLICATION_STATUS.INBEHANDELING]: "bg-brand-subtle text-brand",
    [APPLICATION_STATUS.INGEDIEND]: "bg-[#FFF7D6] text-[#7F5F01]",
    [APPLICATION_STATUS.GOEDGEKEURD]: "bg-[#DCFFF1] text-[#1F845A]",
    [APPLICATION_STATUS.AFGEKEURD]: "bg-[#FFECEB] text-[#AE2A19]",
};

const fmtDateTime = (v) =>
    v ? new Date(v).toLocaleString("nl-NL", { dateStyle: "short", timeStyle: "short" }) : "-";

const AdminAanvraagDetailsPage = (props) => {
    const [AanvraagDetails, setAanvraagDetails] = useState({});
    const [loading, setLoading] = useState(false);
    const [isError, setError] = useState(false);
    const [isNetworkError, setIsNetworkError] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isKvkModalOpen, setIsKvkModalOpen] = useState(false);
    const [isReload, setIsReload] = useState(false);
    const [openEmailLogId, setOpenEmailLogId] = useState(null);
    const [reason, setReason] = useState("");
    const { aanraagId } = useParams();
    const navigate = useNavigate();

    const getAanraagDetails = async () => {
        setLoading(true);
        try {
            const data = await getApplicationById(aanraagId);
            setAanvraagDetails(data);
        } catch (err) {
            setError(true);
            if (err?.isNetworkError) {
                setIsNetworkError(true);
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (aanraagId) {
            getAanraagDetails();
        }
    }, [aanraagId, isReload]);

    // Standaard aan: zo verandert er niets aan het bestaande gedrag tenzij de
    // admin de mail bewust uitzet.
    const [notifyCustomer, setNotifyCustomer] = useState(true);
    // CRM-laag (zoals BTW): toewijzen, opmerkingen/activiteit, klantmail.
    const { userData } = useUser();
    const [admins, setAdmins] = useState([]);
    const [emailOpen, setEmailOpen] = useState(false);
    const [postingComment, setPostingComment] = useState(false);
    useEffect(() => {
        adminFetchAdmins().then((res) => setAdmins(res?.data ?? res ?? [])).catch(() => {});
    }, []);
    const applyActivityAppend = (activity, lastActivityAt) =>
        setAanvraagDetails((prev) => ({ ...prev, activities: [...(prev?.activities ?? []), activity], lastActivityAt: lastActivityAt ?? prev?.lastActivityAt }));
    const handleAddComment = async (body) => {
        setPostingComment(true);
        try {
            const res = await adminAddApplicationComment(aanraagId, body);
            const payload = res?.data ?? res;
            applyActivityAppend(payload.activity, payload.lastActivityAt);
        } finally {
            setPostingComment(false);
        }
    };
    const handleEditComment = async (commentId, body) => {
        const res = await adminEditApplicationComment(aanraagId, commentId, body);
        const payload = res?.data ?? res;
        setAanvraagDetails((prev) => ({
            ...prev,
            activities: (prev?.activities ?? []).map((a) => (String(a._id) === String(commentId) ? { ...a, body: payload.body ?? body, updatedAt: payload.updatedAt, isEdited: true } : a)),
        }));
    };
    const handleDeleteComment = async (commentId) => {
        await adminDeleteApplicationComment(aanraagId, commentId);
        setAanvraagDetails((prev) => ({ ...prev, activities: (prev?.activities ?? []).filter((a) => String(a._id) !== String(commentId)) }));
    };
    const handleAssigneeChange = async (picked) => {
        try {
            const res = await adminUpdateApplicationAssignee(aanraagId, picked?._id ?? null);
            const updated = res?.data ?? res;
            setAanvraagDetails((prev) => ({ ...prev, assignedToAdminId: updated?.assignedToAdminId ?? picked ?? null }));
            toastrSuccess("Toewijzing bijgewerkt.");
            getAanraagDetails();
        } catch (err) {
            toastrError(err?.response?.data?.error ?? "Toewijzen mislukt.");
            throw err;
        }
    };
    const subsidieMailApi = {
        fetchTemplates: () => adminFetchBtwEmailTemplates({ scope: "subsidie" }),
        fetchMailContext: adminApplicationMailContext,
        sendEmail: adminSendApplicationCustomerEmail,
    };
    const customerEmailAddress = AanvraagDetails?.klantId?.email ?? AanvraagDetails?.email ?? "";
    // Klantfactuur (3.35): handmatig aanmaken na Goedgekeurd.
    const [invoiceConfirmOpen, setInvoiceConfirmOpen] = useState(false);
    const [invoiceNotify, setInvoiceNotify] = useState(true);
    const [invoiceBusy, setInvoiceBusy] = useState(false);
    const klantInvoice = AanvraagDetails?.klantInvoice ?? null;
    const canCreateInvoice = !isPartnerMode && !!AanvraagDetails?.klantId && AanvraagDetails?.status === APPLICATION_STATUS.GOEDGEKEURD && !klantInvoice;
    const createInvoice = async () => {
        setInvoiceBusy(true);
        try {
            const res = await adminCreateApplicationInvoice(aanraagId, { notifyCustomer: invoiceNotify });
            const d = res?.data ?? res;
            if (d?.emailError) toastrError(`Factuur aangemaakt, maar de mail is niet verstuurd: ${d.emailError}`);
            else toastrSuccess(res?.successMsg ?? "Klantfactuur aangemaakt.");
            setInvoiceConfirmOpen(false);
            getAanraagDetails();
        } catch (err) {
            toastrError(err?.response?.data?.error ?? "Factuur aanmaken mislukt.");
        } finally {
            setInvoiceBusy(false);
        }
    };
    // Bordkolom (workflow-status van dit type), los van de RVO-status.
    const [workflowStatuses, setWorkflowStatuses] = useState([]);
    const moduleKey = { "EIA": "eia", "EIA Zon": "eiaZon", "ISDE": "isde" }[AanvraagDetails?.regling] ?? null;
    useEffect(() => {
        if (!moduleKey) return;
        adminFetchBtwStatuses({ module: moduleKey })
            .then((res) => setWorkflowStatuses(res?.data ?? res ?? []))
            .catch(() => {});
    }, [moduleKey]);
    const handleWorkflowChange = async (picked) => {
        if (!picked?._id) return;
        try {
            const res = await adminUpdateApplicationWorkflowStatus(aanraagId, picked._id);
            const updated = res?.data ?? res;
            setAanvraagDetails((prev) => ({ ...prev, workflowStatusId: updated?.workflowStatusId ?? picked }));
            toastrSuccess("Kolom bijgewerkt.");
        } catch (err) {
            toastrError(err?.response?.data?.error ?? "Kolom wijzigen mislukt.");
            throw err;
        }
    };

    const closeDropdown = async (value, reason) => {
        if (value) {
            if (value === APPLICATION_STATUS.AFGEKEURD && !reason) {
                setIsModalOpen(true);
                return;
            }
        }
        await updateStatus(value, reason);
        document.getElementById("dropdown-status")?.removeAttribute("open");
    };

    const updateStatus = async (value, reason) => {
        try {
            setLoading(true);
            const updates = { status: value, notifyCustomer };
            if (value === APPLICATION_STATUS.AFGEKEURD && !reason) {
                return;
            }
            if (reason) {
                updates.afgekeurdReason = reason;
            }
            const res = await adminUpdateApplicationStatus(aanraagId, updates);
            navigate(-1);
            const melding =
                res?.successMsg ||
                res?.message ||
                (notifyCustomer
                    ? "Status bijgewerkt, de klant heeft een e-mail gekregen."
                    : "Status bijgewerkt, zonder e-mail naar de klant.");
            toastrSuccess(melding);
        } catch (err) {
            console.error("Error updating application status:", err);
            toastrError(err?.response?.data?.error || "Error updating application status");
        } finally {
            setLoading(false);
        }
    }

    const isStatusEditable = AanvraagDetails?.status !== APPLICATION_STATUS.AFGEKEURD && AanvraagDetails?.status !== APPLICATION_STATUS.GOEDGEKEURD;

    const isPartnerMode = AanvraagDetails?.billingMode === "partner_invoice" || !!AanvraagDetails?.partnerUserId;
    const partnerNaam = partnerNameOf(AanvraagDetails);
    const partnerInvoiceCfg =
        PARTNER_INVOICE_LABELS[AanvraagDetails?.partnerInvoice?.status ?? "none"] ?? PARTNER_INVOICE_LABELS.none;
    const machtiging = AanvraagDetails?.machtiging;

    // Koppelen/loskoppelen kan zolang er nog niets gefactureerd is. Loskoppelen
    // van een goedgekeurde aanvraag kan niet (de server weigert dat ook): de
    // klant zou dan alsnog een factuur moeten krijgen.
    const [isPartnerModalOpen, setIsPartnerModalOpen] = useState(false);
    const [isUnlinkOpen, setIsUnlinkOpen] = useState(false);
    const [unlinkBusy, setUnlinkBusy] = useState(false);
    const lineStatus = AanvraagDetails?.partnerInvoice?.status;
    const partnerLocked = lineStatus === "invoiced" || lineStatus === "paid";
    const canChangePartner =
        !partnerLocked && AanvraagDetails?.status !== APPLICATION_STATUS.AFGEKEURD;
    const canUnlinkPartner =
        canChangePartner &&
        !!AanvraagDetails?.klantId &&
        AanvraagDetails?.status !== APPLICATION_STATUS.GOEDGEKEURD;

    const onPartnerSaved = (updated, partner) => {
        if (updated && updated._id) setAanvraagDetails(updated);
        else setIsReload((v) => !v);
        toastrSuccess(`Aanvraag gekoppeld aan ${partner?.bedrijfsnaam || partner?.naam || "de partner"}.`);
    };

    const unlinkPartner = async () => {
        setUnlinkBusy(true);
        try {
            const updated = await adminSetApplicationPartner(aanraagId, null);
            if (updated && updated._id) setAanvraagDetails(updated);
            else setIsReload((v) => !v);
            setIsUnlinkOpen(false);
            toastrSuccess("Aanvraag losgekoppeld van de partner. De klant krijgt weer een eigen factuur.");
        } catch (err) {
            toastrError(err?.response?.data?.error || "Loskoppelen is mislukt.");
        } finally {
            setUnlinkBusy(false);
        }
    };

    const formatNumber = (number) => {
        return number.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const getKvkData = async () => {
        setLoading(true);
        await fetchUserCompanyDetails(AanvraagDetails?.KvkNummer).then(data => {
            setLoading(false);
            setIsReload(!isReload);
        }).catch(err => {
          toastrError(err?.response?.data?.error || "Er is een fout opgetreden. Probeer het later opnieuw.");
          setLoading(false);

        }).finally(() => {
           
        });
      }

      function formatDate(dateString) {
        const [year, month, day] = dateString.split('-');
        return `${day}-${month}-${year}`;
      }

      
    if (loading) {
        return <PageLoader />;
    }

    if (isError) {
        return (
            <div className="main-wrapper">
                <div className="error-box">
                    <h1>{isNetworkError ? "503" : "404"}</h1>
                    <h3 className="h2 mb-3">
                        <i className="fas fa-exclamation-circle" /> Oops! {isNetworkError ? "Network Error!" : ""}
                    </h3>
                    <p className="h4 font-weight-normal mt-2">
                        {isNetworkError ? "Network Error" : "Er is iets misgegaan. Probeer het later opnieuw"}
                    </p>
                    <button onClick={() => navigate(-1)} className="mt-2 inline-flex h-8 items-center rounded bg-[#091E420F] px-3 text-sm font-medium text-ink hover:bg-[#091E4224]">
                        Ga terug
                    </button>
                </div>
            </div>
        );
    }

    const statusLabel = (st) => (st === APPLICATION_STATUS.INGEDIEND ? 'Ingediend bij RVO' : st);
    const statusLozenge =
        STATUS_LOZENGE[AanvraagDetails?.status] ?? "bg-[#091E420F] text-ink-subtle";
    const regelingRoute =
        AanvraagDetails?.regling === "EIA Zon"
            ? "/panel/admin/eia-zon-overzicht"
            : AanvraagDetails?.regling === "ISDE"
            ? "/panel/admin/isde-overzicht"
            : "/panel/admin/eia-overzicht";

    return (
        <>
            <div className="page-wrapper">
                <div className="content aanvraag-details-page">
                    {/* Kop: breadcrumb, titel, status */}
                    <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
                        <div className="min-w-0">
                            <nav className="mb-1 text-xs text-ink-subtlest" aria-label="Breadcrumb">
                                <Link to="/panel/admin/dashboard" className="hover:underline">Dashboard</Link>
                                <span className="mx-1">/</span>
                                <Link to={regelingRoute} className="hover:underline">{AanvraagDetails?.regling || "Subsidie"} Aanvragen</Link>
                                <span className="mx-1">/</span>
                                <span>{AanvraagDetails?.issueKey || AanvraagDetails?.Kenmerk || "Aanvraag"}</span>
                            </nav>
                            <h1 className="flex flex-wrap items-center gap-2 text-2xl font-medium text-ink">
                                <span className="truncate">{AanvraagDetails?.VoorLetters + " " + AanvraagDetails?.achterNaam}</span>
                                <span className={`inline-flex items-center rounded px-1.5 py-px text-[11px] font-bold uppercase tracking-wide ${statusLozenge}`}>
                                    {statusLabel(AanvraagDetails?.status)}
                                </span>
                                {isPartnerMode ? (
                                    <span className="inline-flex items-center rounded bg-[#F3F0FF] px-1.5 py-px text-[11px] font-bold uppercase tracking-wide text-[#5E4DB2]">
                                        Partner: {partnerNaam || "onbekend"}
                                    </span>
                                ) : null}
                            </h1>
                            <p className="mt-1 text-sm text-ink-subtlest">
                                {AanvraagDetails?.regling} · Kenmerk {AanvraagDetails?.Kenmerk}
                                {AanvraagDetails?.handelsNaam ? ` · ${AanvraagDetails.handelsNaam}` : ""}
                            </p>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                            <button
                                type="button"
                                onClick={() => setEmailOpen(true)}
                                disabled={!customerEmailAddress}
                                title={!customerEmailAddress ? "Geen e-mailadres bekend" : ""}
                                className="inline-flex h-8 items-center gap-1.5 rounded bg-brand px-3 text-sm font-medium text-white hover:bg-brand-hover disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                <FeatherIcon icon="mail" size={16} />
                                Mail klant
                            </button>
                            <button
                                type="button"
                                onClick={() => navigate(-1)}
                                className="inline-flex h-8 items-center gap-1.5 rounded bg-[#091E420F] px-3 text-sm font-medium text-ink hover:bg-[#091E4224]"
                            >
                                <FeatherIcon icon="arrow-left" size={16} />
                                Terug
                            </button>
                        </div>
                    </div>

                    {AanvraagDetails?.status == APPLICATION_STATUS.AFGEKEURD && (
                        <div className="mb-6 flex items-start gap-2 rounded bg-[#FFECEB] px-3 py-2 text-sm text-[#AE2A19]">
                            <FeatherIcon icon="alert-circle" size={16} className="mt-0.5 flex-shrink-0" />
                            <div>
                                <p className="font-semibold">Reden voor Afgekeurdt</p>
                                <p>{AanvraagDetails?.afgekeurdReason}</p>
                            </div>
                        </div>
                    )}

                    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
                        {/* Linkerkolom: inhoud */}
                        <div className="min-w-0 space-y-6">
                            <section className="rounded bg-white p-5 shadow-card">
                                <h2 className="mb-4 text-base font-semibold text-ink">Persoonlijke gegevens</h2>
                                <div className="grid grid-cols-1 gap-x-8 gap-y-4 md:grid-cols-2">
                                    <div className="left-items space-y-4">
                                        <div>
                                            <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-subtlest">Naam</p>
                                            <p className="text-sm text-ink">{
                                                AanvraagDetails?.VoorLetters + " " + AanvraagDetails?.achterNaam
                                            }</p>
                                        </div>
                                        <div>
                                            <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-subtlest">Geslacht</p>
                                            <p className="text-sm text-ink">{AanvraagDetails?.geslacht}</p>
                                        </div>
                                        <div>
                                            <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-subtlest">Postcode</p>
                                            <p className="text-sm text-ink">{AanvraagDetails?.postCode}</p>
                                        </div>
                                        <div>
                                            <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-subtlest">Woonplaats</p>
                                            <p className="text-sm text-ink">{AanvraagDetails?.plaats}</p>
                                        </div>
                                        <div>
                                            <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-subtlest">Adres</p>
                                            <p className="text-sm text-ink">{AanvraagDetails?.straatNaam} {AanvraagDetails?.huisNummer}</p>
                                        </div>

                                        { AanvraagDetails?.isKvkDetailsAvailable &&
                                         <button
                                          type="button"
                                          onClick={() => setIsKvkModalOpen(true)}
                                          className="inline-flex h-8 items-center gap-1.5 rounded bg-[#091E420F] px-3 text-sm font-medium text-ink hover:bg-[#091E4224]"
                                         >
                                         <FeatherIcon icon="eye" size={16} /> View Kvk Details
                                        </button>
                                        }
                                        {
                                            !AanvraagDetails?.isKvkDetailsAvailable && <p className="text-xs text-[#AE2A19]">Kvk details not available</p>
                                        }
                                        {
                                           !AanvraagDetails?.isKvkDetailsAvailable && <button
                                            type="button"
                                            onClick={() => getKvkData()}
                                            className="inline-flex h-8 items-center gap-1.5 rounded bg-[#091E420F] px-3 text-sm font-medium text-ink hover:bg-[#091E4224]"
                                           >
                                           <FeatherIcon icon="arrow-down-circle" size={16} /> Get Kvk Details
                                          </button>
                                        }
                                    </div>
                                    <div className="right-items space-y-4">
                                        <div>
                                            <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-subtlest">Telefoonnummer</p>
                                            <p className="text-sm text-ink">{AanvraagDetails?.teleFoonNummer}</p>
                                        </div>
                                        <div>
                                            <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-subtlest">E-mail</p>
                                            <p className="text-sm text-ink">{AanvraagDetails?.email}</p>
                                        </div>
                                        <div>
                                            <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-subtlest">KVK-nummer</p>
                                            <p className="text-sm text-ink">{AanvraagDetails?.KvkNummer}</p>
                                        </div>
                                        <div>
                                            <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-subtlest">Handelsnaam</p>
                                            <p className="text-sm text-ink">{AanvraagDetails?.handelsNaam}</p>
                                        </div>
                                        {AanvraagDetails?.rechtsvorm && (
                                            <div>
                                                <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-subtlest">Rechtsvorm</p>
                                                <p className="text-sm text-ink">{AanvraagDetails?.uitgebreideRechtsvorm || AanvraagDetails?.rechtsvorm}</p>
                                            </div>
                                        )}
                                        {AanvraagDetails?.rsin && (
                                            <div>
                                                <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-subtlest">RSIN</p>
                                                <p className="text-sm text-ink">{AanvraagDetails?.rsin}</p>
                                            </div>
                                        )}
                                        {/* Alleen gevuld bij een eenmanszaak; die heeft geen RSIN. */}
                                        {AanvraagDetails?.bsn && (
                                            <div>
                                                <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-subtlest">BSN eigenaar</p>
                                                <p className="text-sm tabular-nums text-ink">{AanvraagDetails?.bsn}</p>
                                            </div>
                                        )}

                                        {
                                            ["EIA", "EIA Zon"].includes(AanvraagDetails?.regling) && <div>
                                            <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-subtlest">Bedrijfsmiddelcodes</p>
                                            {
                                                AanvraagDetails?.bedrijfsmiddelCode?.map((item, index) => {
                                                    return <p key={index} className="text-sm text-ink">{item}</p>
                                                })
                                            }
                                        </div>
                                        }

                                       {
                                        AanvraagDetails?.regling === "ISDE" && <div>
                                            <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-subtlest">meldCodes</p>
                                            {
                                                AanvraagDetails?.meldCodes?.map((item, index) => {
                                                    return <p key={index} className="text-sm text-ink">{item}</p>
                                                })
                                            }
                                        </div>
                                        }
                                    </div>
                                </div>
                            </section>

                            <section className="rounded bg-white p-5 shadow-card">
                                <h2 className="mb-4 text-base font-semibold text-ink">Subsidie ({AanvraagDetails?.regling})</h2>
                                <div className="left-items space-y-4">
                                    {AanvraagDetails?.regling === "EIA Zon" && (
                                        <>
                                            <div>
                                                <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-subtlest">Datum opdrachtbevestiging</p>
                                                <p className="text-sm text-ink">{ formatDate(AanvraagDetails?.datumOpdrachtBevestiging) }</p>
                                            </div>
                                            <div>
                                                <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-subtlest">Aantal zonnepanelen</p>
                                                <p className="text-sm text-ink">{AanvraagDetails?.aantalZonnepanelen}</p>
                                            </div>
                                            <div>
                                                <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-subtlest">Totaal vermogen (Wp)</p>
                                                <p className="text-sm text-ink">{AanvraagDetails?.totaalVermogen}</p>
                                            </div>
                                        </>
                                    )}
                                    {["EIA", "ISDE"].includes(AanvraagDetails?.regling) && (
                                        <div>
                                            <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-subtlest">Datum opdrachtbevestiging</p>
                                            <p className="text-sm text-ink">{ formatDate(AanvraagDetails?.datumOpdrachtBevestiging) }</p>
                                        </div>
                                    )}
                                    {AanvraagDetails?.regling === "ISDE" && (
                                        <div>
                                            <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-subtlest">Type</p>
                                            <p className="text-sm text-ink">{AanvraagDetails?.type}</p>
                                        </div>
                                    )}

                                    <div>
                                        { AanvraagDetails?.regling === "ISDE" && AanvraagDetails?.type === 'warmtepomp' && <a
                                            href={AanvraagDetails?.annualStatement}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex h-8 items-center gap-1.5 rounded bg-[#091E420F] px-3 text-sm font-medium text-ink hover:bg-[#091E4224]"
                                        >
                                            <FeatherIcon icon="download" size={16} />
                                            Jaarnota
                                        </a>
                                        }
                                    </div>

                                    {
                                       !!AanvraagDetails?.quotationCopyFiles?.length &&
                                      <div>
                                        <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-ink-subtlest">Offerte</p>
                                        <div className="flex flex-row flex-wrap gap-2">
                                            {AanvraagDetails?.quotationCopyFiles?.map((url, index) => {
                                                const fileName = url.split('/').pop();
                                                return <a
                                                    key={index}
                                                    href={url} // Replace with the actual base URL or path where your files are stored
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="inline-flex h-8 items-center gap-1.5 rounded bg-[#091E420F] px-3 text-sm font-medium text-ink hover:bg-[#091E4224]"
                                                >
                                                    <FeatherIcon icon="download" size={16} /> {fileName?.length > 20 ? fileName?.slice(0, 15) + "..." : fileName}
                                                </a>
                                            })}
                                        </div>
                                      </div>
                                    }
                                </div>
                            </section>

                            <section className="rounded bg-white p-5 shadow-card">
                                <h2 className="mb-4 text-base font-semibold text-ink">Aantekeningen (Intern)</h2>
                                <div className="left-items">
                                    <p className="whitespace-pre-line text-sm text-ink">{AanvraagDetails?.aantekeningVoorOns || "Geen aantekingen"}</p>
                                </div>
                            </section>

                            {machtiging?.signedAt ? (
                                <section className="rounded bg-white p-5 shadow-card">
                                    <h2 className="mb-4 text-base font-semibold text-ink">Akkoord &amp; handtekening</h2>
                                    <div className="flex flex-wrap items-start justify-between gap-6">
                                        <div>
                                            <p>
                                                <span className="inline-flex items-center rounded bg-[#DCFFF1] px-1.5 py-px text-[11px] font-bold uppercase tracking-wide text-[#1F845A]">
                                                    {machtiging?.akkoord ? "Akkoord gegeven" : "Ondertekend"}
                                                </span>
                                            </p>
                                            <p className="mt-2 text-sm text-ink">Ondertekend op: <strong className="font-medium">{fmtDateTime(machtiging?.signedAt)}</strong></p>
                                            <p className="mt-1 text-xs text-ink-subtlest">
                                                Machtiging tot het indienen van de subsidieaanvraag namens de onderneming.
                                            </p>
                                        </div>
                                        {machtiging?.signatureFile?.url ? (
                                            <a
                                                href={machtiging.signatureFile.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                title="Open origineel"
                                                className="inline-block rounded border border-line bg-white p-2"
                                            >
                                                <img
                                                    src={machtiging.signatureFile.url}
                                                    alt="Handtekening"
                                                    className="h-24 w-auto max-w-[320px] object-contain"
                                                />
                                            </a>
                                        ) : null}
                                    </div>
                                </section>
                            ) : null}

                            {/* Alle mail die het systeem voor dit dossier heeft verstuurd
                                (bevestiging, statuswijzigingen, klantmail, factuur, intern).
                                Bron: EmailLogs. Ververst mee met de pagina (isReload) en
                                na een handmatige klantmail (emailOpen sluit). */}
                            <section className="rounded bg-white p-5 shadow-card">
                                <h2 className="mb-4 text-base font-semibold text-ink">Verstuurde mails</h2>
                                <EmailLogList
                                    applicationId={aanraagId}
                                    applicationModel="Applications"
                                    compact
                                    limit={10}
                                    reloadKey={`${isReload ? 1 : 0}-${emailOpen ? 1 : 0}`}
                                    onOpen={setOpenEmailLogId}
                                />
                            </section>
                            <BtwActivityFeed
                                activities={AanvraagDetails?.activities ?? []}
                                onAddComment={handleAddComment}
                                onEditComment={handleEditComment}
                                onDeleteComment={handleDeleteComment}
                                posting={postingComment}
                                currentUserId={userData?._id}
                                currentUserPhoto={userData?.profielFoto}
                                statuses={workflowStatuses}
                                admins={admins}
                            />
                        </div>

                        {/* Rechterkolom: details */}
                        <aside className="space-y-6">
                            <section className="rounded bg-white p-5 shadow-card">
                                <h2 className="mb-4 text-base font-semibold text-ink">Details</h2>
                                <dl className="space-y-4">
                                    <div>
                                        <dt className="text-[11px] font-semibold uppercase tracking-wide text-ink-subtlest">Status van aanvraag</dt>
                                        <dd className="mt-1">
                                            {isStatusEditable ? (
                                                <>
                                                    <details className="dropdown-action dropdown" id="dropdown-status">
                                                        <summary className={`inline-flex h-8 cursor-pointer list-none items-center gap-1.5 rounded px-2 text-[11px] font-bold uppercase tracking-wide ${statusLozenge}`}>
                                                            {statusLabel(AanvraagDetails?.status)}
                                                            <FeatherIcon icon="chevron-down" size={14} />
                                                        </summary>
                                                        <ul className="dropdown-content z-[1] flex w-[200px] flex-col rounded bg-white p-1 shadow-overlay">
                                                            <a className="link-ancher cursor-pointer rounded px-2 py-1.5 text-sm text-ink hover:bg-[#091E420F]" onClick={() => { closeDropdown(APPLICATION_STATUS.GOEDGEKEURD) }}>{APPLICATION_STATUS.GOEDGEKEURD}</a>
                                                            <a className="link-ancher cursor-pointer rounded px-2 py-1.5 text-sm text-ink hover:bg-[#091E420F]" onClick={() => { closeDropdown(APPLICATION_STATUS.INGEDIEND) }}>
                                                                Ingediend bij RVO
                                                            </a>
                                                            <a className="link-ancher cursor-pointer rounded px-2 py-1.5 text-sm text-ink hover:bg-[#091E420F]" onClick={() => { closeDropdown(APPLICATION_STATUS.AFGEKEURD) }}>{APPLICATION_STATUS.AFGEKEURD}</a>
                                                        </ul>
                                                    </details>
                                                    <label className="mt-2 flex items-center gap-2 text-xs text-ink-subtle">
                                                        <input
                                                            type="checkbox"
                                                            checked={notifyCustomer}
                                                            onChange={(e) => setNotifyCustomer(e.target.checked)}
                                                            className="h-4 w-4 rounded border-line text-brand focus:ring-0"
                                                        />
                                                        Klant een e-mail sturen over deze wijziging
                                                    </label>
                                                </>
                                            ) : (
                                                <span className={`inline-flex items-center rounded px-1.5 py-px text-[11px] font-bold uppercase tracking-wide ${statusLozenge}`}>
                                                    {AanvraagDetails?.status}
                                                </span>
                                            )}
                                        </dd>
                                    </div>
                                    <div>
                                        <dt className="text-[11px] font-semibold uppercase tracking-wide text-ink-subtlest">Toegewezen aan</dt>
                                        <dd className="mt-1">
                                            <BtwAssigneeMenu
                                                current={AanvraagDetails?.assignedToAdminId && typeof AanvraagDetails.assignedToAdminId === "object" ? AanvraagDetails.assignedToAdminId : null}
                                                admins={admins}
                                                currentUserId={userData?._id}
                                                onSelect={handleAssigneeChange}
                                                align="right"
                                            />
                                        </dd>
                                    </div>
                                    {moduleKey ? (
                                        <div>
                                            <dt className="text-[11px] font-semibold uppercase tracking-wide text-ink-subtlest">Kolom op het bord</dt>
                                            <dd className="mt-1">
                                                <BtwStatusMenu
                                                    current={AanvraagDetails?.workflowStatusId && typeof AanvraagDetails.workflowStatusId === "object" ? AanvraagDetails.workflowStatusId : null}
                                                    options={workflowStatuses}
                                                    onSelect={handleWorkflowChange}
                                                    align="right"
                                                />
                                                <p className="mt-1 text-xs text-ink-subtlest">Kolommen die aan een RVO-status gekoppeld zijn, wijzig je via &ldquo;Status van aanvraag&rdquo;.</p>
                                            </dd>
                                        </div>
                                    ) : null}
                                    <div>
                                        <dt className="text-[11px] font-semibold uppercase tracking-wide text-ink-subtlest">Kenmerk klant</dt>
                                        <dd className="mt-1 text-sm text-ink">{AanvraagDetails?.Kenmerk}</dd>
                                        {AanvraagDetails?.issueKey ? (
                                            <dd className="mt-1 text-xs font-medium text-ink-subtlest" title="Key op het bord">
                                                {AanvraagDetails.issueKey}
                                            </dd>
                                        ) : null}
                                    </div>
                                    <div>
                                        <dt className="text-[11px] font-semibold uppercase tracking-wide text-ink-subtlest">Klantnaam</dt>
                                        <dd className="mt-1 text-sm text-ink">{AanvraagDetails?.VoorLetters + " " + AanvraagDetails?.achterNaam}</dd>
                                        {isPartnerMode ? (
                                            <dd className="mt-1">
                                                <span className="inline-flex items-center rounded bg-[#F3F0FF] px-1.5 py-px text-[11px] font-bold uppercase tracking-wide text-[#5E4DB2]">
                                                    Partner: {partnerNaam || "onbekend"}
                                                </span>
                                            </dd>
                                        ) : null}
                                    </div>
                                    <div>
                                        <dt className="text-[11px] font-semibold uppercase tracking-wide text-ink-subtlest">Regeling</dt>
                                        <dd className="mt-1 text-sm text-ink">{AanvraagDetails?.regling}</dd>
                                    </div>
                                </dl>
                            </section>

                            {isPartnerMode ? (
                                <section className="rounded bg-white p-5 shadow-card">
                                    <h2 className="mb-4 text-base font-semibold text-ink">Facturatie via partner</h2>
                                    <div>
                                        <p className="text-sm text-ink">Partner: <strong className="font-medium">{partnerNaam || "-"}</strong></p>
                                        <p className="mt-2 flex items-center gap-2 text-sm text-ink">
                                            Factuurregel:{" "}
                                            <span className={`inline-flex items-center rounded px-1.5 py-px text-[11px] font-bold uppercase tracking-wide ${partnerInvoiceCfg.color}`}>
                                                {AanvraagDetails?.partnerInvoice?.status ? partnerInvoiceCfg.label : "Nog niet goedgekeurd"}
                                            </span>
                                        </p>
                                        <p className="mt-2 text-xs text-ink-subtlest">
                                            De klant betaalt niets. Na goedkeuring komt deze aanvraag als regel op de partner-factuur
                                            (€{formatNumber(AanvraagDetails?.exclusiveBTW || '0')} excl. btw).
                                        </p>
                                        {AanvraagDetails?.klantId ? (
                                            <p className="mt-1 text-xs text-ink-subtlest">
                                                Ingediend via het portaal door {AanvraagDetails?.klantId?.naam || "de klant"}, daarna aan de partner gekoppeld.
                                            </p>
                                        ) : null}
                                        {canChangePartner ? (
                                            <div className="mt-3 flex flex-wrap gap-2">
                                                <button
                                                    type="button"
                                                    onClick={() => setIsPartnerModalOpen(true)}
                                                    className="inline-flex h-8 items-center rounded bg-[#091E420F] px-3 text-sm font-medium text-ink hover:bg-[#091E4224]"
                                                >
                                                    Andere partner
                                                </button>
                                                {canUnlinkPartner ? (
                                                    <button
                                                        type="button"
                                                        onClick={() => setIsUnlinkOpen(true)}
                                                        className="inline-flex h-8 items-center rounded bg-[#FFECEB] px-3 text-sm font-medium text-[#AE2A19] hover:bg-[#FFD5D2]"
                                                    >
                                                        Loskoppelen
                                                    </button>
                                                ) : null}
                                            </div>
                                        ) : partnerLocked ? (
                                            <p className="mt-2 text-xs text-ink-subtlest">
                                                Staat al op een partner-factuur; de koppeling kan niet meer gewijzigd worden.
                                            </p>
                                        ) : null}
                                    </div>
                                </section>
                            ) : (
                                <section className="rounded bg-white p-5 shadow-card">
                                    <h2 className="mb-4 text-base font-semibold text-ink">Factuurinformatie</h2>
                                    <dl className="space-y-3">
                                        <div>
                                            <dt className="text-[11px] font-semibold uppercase tracking-wide text-ink-subtlest">Factuur aan</dt>
                                            <dd className="mt-1 text-sm text-ink">{AanvraagDetails?.klantId?.naam}</dd>
                                        </div>
                                        <div>
                                            <dt className="text-[11px] font-semibold uppercase tracking-wide text-ink-subtlest">Bedrag exclusief BTW</dt>
                                            <dd className="mt-1 text-sm tabular-nums text-ink">€{formatNumber(AanvraagDetails?.exclusiveBTW || '0')}</dd>
                                        </div>
                                        <div>
                                            <dt className="text-[11px] font-semibold uppercase tracking-wide text-ink-subtlest">Bedrag inclusief BTW</dt>
                                            <dd className="mt-1 text-sm tabular-nums text-ink">€{formatNumber(AanvraagDetails?.totaal || '0')}</dd>
                                        </div>
                                        <div>
                                            <dt className="text-[11px] font-semibold uppercase tracking-wide text-ink-subtlest">Klantfactuur</dt>
                                            <dd className="mt-1 text-sm text-ink">
                                                {klantInvoice ? (
                                                    <span className="inline-flex flex-wrap items-center gap-2">
                                                        <Link to="/panel/admin/financieel" className="text-brand hover:underline">{klantInvoice.InvoiceId}</Link>
                                                        <span className={`inline-flex items-center rounded px-1.5 py-px text-[11px] font-bold uppercase tracking-wide ${klantInvoice.status === "Betaald" ? "bg-[#DCFFF1] text-[#1F845A]" : "bg-[#FFF7D6] text-[#7F5F01]"}`}>
                                                            {klantInvoice.status}
                                                        </span>
                                                    </span>
                                                ) : AanvraagDetails?.status === APPLICATION_STATUS.GOEDGEKEURD ? (
                                                    <span className="text-ink-subtlest">Nog niet aangemaakt</span>
                                                ) : (
                                                    <span className="text-ink-subtlest">Na goedkeuring aan te maken</span>
                                                )}
                                            </dd>
                                        </div>
                                    </dl>
                                    {canCreateInvoice ? (
                                        <div className="mt-4 space-y-2">
                                            <button
                                                type="button"
                                                onClick={() => setInvoiceConfirmOpen(true)}
                                                className="inline-flex h-8 items-center gap-1.5 rounded bg-brand px-3 text-sm font-medium text-white hover:bg-brand-hover"
                                            >
                                                <FeatherIcon icon="file-text" size={16} />
                                                Maak klantfactuur
                                            </button>
                                            <label className="flex items-center gap-2 text-xs text-ink-subtle">
                                                <input type="checkbox" checked={invoiceNotify} onChange={(e) => setInvoiceNotify(e.target.checked)} className="h-4 w-4 rounded border-line text-brand focus:ring-0" />
                                                Klant een e-mail sturen met de factuurlink
                                            </label>
                                        </div>
                                    ) : null}
                                    {canChangePartner ? (
                                        <div className="mt-4">
                                            <button
                                                type="button"
                                                onClick={() => setIsPartnerModalOpen(true)}
                                                className="inline-flex h-8 items-center rounded bg-[#F3F0FF] px-3 text-sm font-medium text-[#5E4DB2] hover:bg-[#DFD8FD]"
                                            >
                                                Koppel aan partner
                                            </button>
                                            <p className="mt-2 text-xs text-ink-subtlest">
                                                Kwam deze klant via een installateur? Dan factureren we de partner in plaats van de klant.
                                            </p>
                                        </div>
                                    ) : null}
                                </section>
                            )}
                        </aside>
                    </div>

                    <EmailPreviewModal
                        isOpen={!!openEmailLogId}
                        emailLogId={openEmailLogId}
                        onClose={() => setOpenEmailLogId(null)}
                    />
                    <AdminRejectAanvraagModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onSubmit={(res) => { res ? closeDropdown(APPLICATION_STATUS.AFGEKEURD, res) : null }} />
                    <SendCustomerEmailModal
                        isOpen={emailOpen}
                        applicationId={aanraagId}
                        customerName={`${AanvraagDetails?.VoorLetters ?? ""} ${AanvraagDetails?.achterNaam ?? ""}`.trim() || "deze klant"}
                        customerEmail={customerEmailAddress}
                        onClose={() => setEmailOpen(false)}
                        onSent={() => getAanraagDetails()}
                        api={subsidieMailApi}
                    />
                    <ConfirmModal
                        isOpen={invoiceConfirmOpen}
                        title="Klantfactuur aanmaken?"
                        message={`Er wordt een factuur van €${formatNumber(AanvraagDetails?.totaal || '0')} aangemaakt voor ${AanvraagDetails?.klantId?.naam || "de klant"}${invoiceNotify ? ", met een e-mail met de factuurlink" : ", zonder e-mail"}.`}
                        confirmLabel="Aanmaken"
                        busy={invoiceBusy}
                        onConfirm={createInvoice}
                        onCancel={() => !invoiceBusy && setInvoiceConfirmOpen(false)}
                    />
                    <KvkDetailsModal isOpen={isKvkModalOpen}  onClose={() => setIsKvkModalOpen(false)} kvkDetails={AanvraagDetails?.kvkDetails} />
                    <ApplicationPartnerModal
                        isOpen={isPartnerModalOpen}
                        application={AanvraagDetails}
                        onClose={() => setIsPartnerModalOpen(false)}
                        onSaved={onPartnerSaved}
                    />
                    <ConfirmModal
                        isOpen={isUnlinkOpen}
                        title="Loskoppelen van partner?"
                        message={`De aanvraag gaat terug naar de gewone klantflow: ${AanvraagDetails?.klantId?.naam || "de klant"} betaalt zelf (maak na goedkeuring een klantfactuur) en de partner ziet de aanvraag niet meer.`}
                        confirmLabel="Loskoppelen"
                        variant="danger"
                        busy={unlinkBusy}
                        onConfirm={unlinkPartner}
                        onCancel={() => { if (!unlinkBusy) setIsUnlinkOpen(false); }}
                    />
                </div>
            </div>
        </>
    );
};

export default AdminAanvraagDetailsPage;
